import { Box, Center, Spinner, Text } from "@chakra-ui/react";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useContext, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router";

import { VaultContext } from "@equidefi/portals/context/VaultProvider";
import { useDocumentTitle } from "@equidefi/portals/hooks/useDocumentTitle";
import { useGetInquiry } from "@equidefi/portals/hooks/useInquiries";
import { useStartSignatureRequest } from "@equidefi/portals/hooks/useSignatures";
import { formatSecurities, userFullName } from "@equidefi/shared";
import { JOINT_SUB_TYPE } from "@equidefi/shared/constants/profile";
import { capitalizeEveryWord } from "@equidefi/shared/helpers/string";
import { DisplayTaxID, TooltipTime } from "@equidefi/ui";
import { Icon } from "@equidefi/ui/icon";

import { InvestmentStatusView } from "@/components/offerings/StatusView";
import ReviewCard from "@/components/workflow/ReviewCard";
import { WorkflowButton } from "@/components/workflow/WorkflowButton";
import {
  useEmbedInvestmentInquiry,
  useSecondariesInquiry,
} from "@/hooks/useInquiries";
import useWorkflowPosition from "@/hooks/useWorkflowPosition";
import { useWorkflowContext } from "@/pages/workflow/context/WorkflowContext";

import { WorkflowContainer } from "../WorkflowContainer";

const InquiryStatusView = ({
  inquiryStatus,
  isLoading: _isLoading,
  kyckyb,
}) => {
  const { investment, offering, options, refetchInvestment } =
    useWorkflowContext();
  const { refetch: refetchVault } = useContext(VaultContext);

  useDocumentTitle([
    "Verify Your Identity",
    offering?.name,
    investment?.issuer?.name,
  ]);
  const navigate = useNavigate();
  const [started, setStarted] = useState(false);
  const signatureRequest = useStartSignatureRequest();
  const secondaries = useSecondariesInquiry(investment.id);
  const { onStart, isLoading: isEmbedLoading } = useEmbedInvestmentInquiry(
    investment?.id,
    {
      onComplete: async () => {
        await secondaries.mutateAsync();
        navigate(`/offerings/${offering.slug}/${investment.id}/agreement`);
      },
      onCancel: async () => {
        await refetchVault();
      },
    },
  );

  useEffect(() => {
    refetchInvestment();
  }, [refetchInvestment]);

  const onSubmit = async () => {
    // Prevent double firing onClick
    if (isLoading) {
      return;
    }

    setStarted(true);

    // Should always come before starting the embed
    await signatureRequest.mutateAsync({ investmentId: investment.id });

    // Start Persona Embed
    await onStart();

    await refetchInvestment();

    setStarted(false);
  };

  const isLoading = useMemo(
    () => isEmbedLoading || _isLoading || started || secondaries.isLoading,
    [_isLoading, isEmbedLoading, started, secondaries.isLoading],
  );

  if (!inquiryStatus || !investment) {
    return null;
  }

  const signingParties = investment.signing_parties.filter(
    (signer) => signer.countersigner === false && signer.order !== 1,
  );

  const accountType = useMemo(() => {
    switch (investment.vault.ta_account_type) {
      case "INDIVIDUAL":
        return "Individual Investor";
      case "JOINT":
        return `${JOINT_SUB_TYPE[investment.vault.ta_sub_type]} (${
          investment.vault.ta_sub_type
        })`;
      case "TRUST":
        return investment.vault.ta_sub_type;
      case "ENTITY":
        return `Entity - ${investment.vault.ta_sub_type}`;
    }
    if (investment.vault.entity_type === "INDIVIDUAL") {
      return `Individual - ${investment.vault.ta_sub_type}`;
    }
    return investment.vault.ta_sub_type;
  }, [investment.vault]);

  const sharesIssuedTo = useMemo(() => {
    switch (investment.vault.ta_account_type) {
      case "INDIVIDUAL":
        return userFullName(investment.vault);
      case "JOINT":
        return `${userFullName(investment.vault)} and ${userFullName(
          investment.signing_parties[2],
        )}`;
      case "TRUST":
      case "ENTITY":
        return investment.vault.company;
    }
    return null;
  }, [investment.vault, investment.signing_parties]);

  if (inquiryStatus === "unknown") {
    return (
      <>
        <InvestmentStatusView title="Review Your Investment">
          <Text>
            Please make sure these details are correct. You will not be able to
            change them later.
          </Text>
          <ReviewCard title="Issue Securities To">
            <Text mb={0}>{sharesIssuedTo}</Text>
          </ReviewCard>
          <ReviewCard
            title="Investment Amount"
            page={investment?.payment_editable ? "subscription" : null}>
            <Text mb={0}>
              ${investment.amount.toLocaleString("en-US")} in{" "}
              {formatSecurities({
                quantity: investment?.quantity,
                offering,
              }).toString()}
            </Text>
          </ReviewCard>
          <ReviewCard
            title="Payment Method"
            page={investment?.payment_editable ? "payment" : null}>
            <Text mb={0}>{investment.payment_type}</Text>
          </ReviewCard>
          <ReviewCard title="Investor Type" page="profile">
            <Text mb={0}>{accountType}</Text>
          </ReviewCard>
          <ReviewCard title="Citizenship" page="address">
            <Text mb={0}>
              {investment.vault.citizenship === "US_CITIZEN"
                ? "US Citizen"
                : "International"}
            </Text>
          </ReviewCard>
          <ReviewCard title="Address" page="address">
            <Text m={0} p={0}>
              {investment.vault.address_street_1}{" "}
              {investment.vault.address_street_2}
            </Text>
            <Text m={0} p={0}>
              {investment.vault.address_city}, {investment.vault.address_state}{" "}
              {investment.vault.address_postal_code},{" "}
              {investment.vault.address_country_code}
            </Text>
          </ReviewCard>

          {investment.vault.entity_type === "ENTITY" && (
            <ReviewCard
              title={`${capitalizeEveryWord(
                investment.vault.ta_account_type,
              )} Name`}
              page="signers">
              <Text m={0} p={0}>
                {investment.vault.company}
              </Text>
              <DisplayTaxID value={investment.vault?.ein} label="EIN" />
            </ReviewCard>
          )}

          <ReviewCard title="Legal Name (Primary Signer)" page="profile">
            <Text m={0} p={0}>
              {userFullName(investment.vault)}
            </Text>

            <Text m={0} p={0}>
              {investment.vault.phone_number}
            </Text>

            <TooltipTime
              datetime={investment.vault.birthdate}
              format="ll"
              tooltip="l"
            />
            <DisplayTaxID value={investment.vault?.tax_id} label="SSN/TIN" />
          </ReviewCard>

          {investment.vault.ta_account_type !== "INDIVIDUAL" && (
            <ReviewCard title="Additional Signers" page="signers">
              {signingParties.map((signer) => (
                <Box mb={3} key={signer.id}>
                  <Text m={0} p={0} fontWeight="bold">
                    {userFullName(signer)}
                  </Text>
                  <Text m={0} p={0}>
                    {signer.email}
                  </Text>
                  <Text m={0} p={0}>
                    {signer.phone}
                  </Text>
                </Box>
              ))}
              <Text>
                {signingParties.length === 0
                  ? "No additional signers added"
                  : null}
              </Text>
            </ReviewCard>
          )}
        </InvestmentStatusView>
        <WorkflowButton isLoading={isLoading} as={Link} onClick={onSubmit}>
          Start
        </WorkflowButton>
      </>
    );
  }

  if (inquiryStatus === "approved") {
    return (
      <>
        <InvestmentStatusView title="Approved!" icon={Icon.CheckCircle}>
          Well done! It looks like your{" "}
          {kyckyb === "KYB" ? "business verification" : "individual identity"}{" "}
          check was approved. You can continue to proceed with our investment.
        </InvestmentStatusView>
        <WorkflowButton
          isLoading={isLoading}
          as={Link}
          to={`/offerings/${offering.slug}/${investment.id}/agreement`}>
          Continue
        </WorkflowButton>
      </>
    );
  }

  if (inquiryStatus === "completed") {
    if (kyckyb === "KYB")
      return (
        <>
          <InvestmentStatusView title="Business verification started...">
            You have been sent an email with a request to complete your
            individual identity check. Your business verification has been
            started, and you will be notified by email once your business
            verification check has been reviewed.
          </InvestmentStatusView>
          <WorkflowButton
            isLoading={isLoading}
            as={Link}
            to={`/offerings/${offering.slug}/${investment.id}/agreement`}>
            Continue
          </WorkflowButton>
        </>
      );

    if (kyckyb === "KYC")
      return (
        <>
          <InvestmentStatusView title="Processing...">
            Your identity check has been completed, but is still processing.
            Please standby...
          </InvestmentStatusView>
          <Center>
            <Spinner />
          </Center>
        </>
      );
  }

  if (inquiryStatus === "created" || inquiryStatus === "pending") {
    return (
      <>
        <InvestmentStatusView
          title={`${
            kyckyb === "KYB" ? "Business verification" : "Identity check"
          } started...`}>
          Your {kyckyb === "KYB" ? "business verification" : "identity check"}{" "}
          has been started but has not been completed yet, please click the link
          below to resume.
        </InvestmentStatusView>
        <WorkflowButton isLoading={isLoading} onClick={onStart}>
          Resume
        </WorkflowButton>
      </>
    );
  }

  if (inquiryStatus === "expired" || inquiryStatus === "resumed") {
    return (
      <>
        <InvestmentStatusView
          title={`${
            kyckyb === "KYB" ? "Business verification" : "Identity verification"
          } expired`}
          centered
          muted>
          Your{" "}
          {kyckyb === "KYB" ? "business verification" : "identity verification"}{" "}
          has expired, please click the link below to resume.
        </InvestmentStatusView>
        <WorkflowButton isLoading={isLoading} onClick={onStart}>
          Resume
        </WorkflowButton>
      </>
    );
  }

  if (inquiryStatus === "declined") {
    return (
      <InvestmentStatusView title="Sorry!" icon={Icon.ShieldOff}>
        It looks like your{" "}
        {kyckyb === "KYB" ? "business verification" : "identity verification"}{" "}
        was declined. If you believe this to be in error, please contact the
        issuer of the offering.
      </InvestmentStatusView>
    );
  }

  if (inquiryStatus === "needs_review") {
    return (
      <InvestmentStatusView title="Under Review" icon={Icon.Activity}>
        It looks like your{" "}
        {kyckyb === "KYB" ? "business verification" : "identity verification"}{" "}
        was automatically marked for review. The issuer will review your
        information. You will receive an email when the issuer has accepted or
        declined your information.
      </InvestmentStatusView>
    );
  }
  return null;
};

export const WorkflowKYC = () => {
  const { investment, refetchInvestment } = useWorkflowContext();
  useWorkflowPosition(6);
  const navigate = useNavigate();
  const { vault, refetch: refetchVault } = useContext(VaultContext);
  const { data: inquiry, isFetching } = useGetInquiry(vault?.inquiry_id, {
    refetchInterval: 15_000,
    refetchIntervalInBackground: true,
  });
  const flags = useFlags();
  const location = useLocation();

  useEffect(() => {
    if (flags.iridium && !location.state?.reviewed) navigate("../review");
    refetchVault();
  }, [location.state, flags.iridium]);

  useEffect(() => {
    if (location.state?.refresh) {
      refetchInvestment();
    }
  }, []);

  const kyckyb = investment?.entity_type === "ENTITY" ? "KYB" : "KYC";

  const inquiryStatus = useMemo(
    () => inquiry?.status ?? "unknown",
    [inquiry?.status],
  );

  return (
    <WorkflowContainer>
      <InquiryStatusView
        inquiryStatus={inquiryStatus}
        investment={investment}
        isLoading={isFetching}
        kyckyb={kyckyb}
      />
    </WorkflowContainer>
  );
};
